import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Icons} from '../../assets';
import {Community} from '../../models/community';
import {Opportunity, OpportunityStatus, OpportunityVisibility} from '../../models/opportunity';
import {ApplicantTab} from '../../pages/opportunity-detail/components/applicants-panel';
import {useOpportunity} from '../../contexts/opportunity.provider';
import {Button, ButtonSize, ButtonVariant} from '../button';
import {Card} from '../card';
import {CommunityBadgesRow} from '../community-badges-row';
import {ConfirmationModal, ConfirmationVariant} from '../modals';
import {Dropdown} from '../dropdown';
import {ExpandableText} from '../expandable-text';
import {Heading, HeadingColor, HeadingLevel, HeadingVariant, HeadingWeight} from '../typography/heading';
import {Stack, StackSpacing} from '../layout';
import {StatCard, StatCardVariant} from '../stat-card';
import {Tag, TagSize, TagVariant} from '../tag';
import {TimeStamp} from '../timestamp';

interface OpportunityCardProps {
  onOpportunityDelete?: (opportunityId: string) => void;
  onOpportunityUpdate?: (updatedOpportunity: Opportunity) => void;
  opportunity: Opportunity;
  restrictedToCommunities?: Community[];
}

const OpportunityCardComponent: React.FC<OpportunityCardProps> = ({
  onOpportunityDelete,
  onOpportunityUpdate,
  opportunity,
  restrictedToCommunities = [],
}) => {
  const navigate = useNavigate();
  const {deleteOpportunity} = useOpportunity();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const isDraft = opportunity.status === OpportunityStatus.DRAFT;

  const handleCardClick = () => {
    navigate(`/opportunities/${opportunity.id}`);
  };

  const handlePreviewClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    navigate(`/opportunities/${opportunity.id}/edit?showPreview=true`);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteOpportunity(opportunity.id);
      onOpportunityDelete?.(opportunity.id);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Failed to delete opportunity:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setShowDeleteModal(false);
    }
  };

  const dropdownItems = [
    {
      label: isDeleting ? 'Closing opportunity...' : 'Close opportunity',
      onClick: handleDeleteClick,
      icon: <img src={Icons.XCIRCLE} alt="Close opportunity" className="w-4 h-4" />,
      className: 'text-red-600 hover:bg-red-50',
    },
  ];

  return (
    <>
      <Card
        hoverable
        className={`relative cursor-pointer ${isDraft ? 'border-dashed border-2 border-blue-300 bg-blue-50' : ''}`}
        onClick={isDraft ? handlePreviewClick : handleCardClick}>
        <Stack spacing={StackSpacing.MD}>
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <Heading
                  level={HeadingLevel.H3}
                  variant={HeadingVariant.CARD_TITLE}
                  weight={HeadingWeight.SEMIBOLD}
                  color={HeadingColor.PRIMARY}
                  className="pr-8 break-words">
                  {opportunity.jobTitle}
                </Heading>
              </div>
              <div className="flex items-center gap-2.5 shrink-0 self-start">
                {opportunity.visibility === OpportunityVisibility.PRIVATE ? (
                  restrictedToCommunities.length > 0 ? (
                    <div onClick={e => e.stopPropagation()}>
                      <CommunityBadgesRow badgeSize={24} communities={restrictedToCommunities} maxVisible={3} />
                    </div>
                  ) : (
                    <img src={Icons.VISIBILITY_FAM} alt="Private" className="shrink-0 w-6 h-6" title="Private" />
                  )
                ) : (
                  <img src={Icons.VISIBILITY_PUBLIC} alt="Public" className="shrink-0 w-6 h-6" title="Public" />
                )}
                <TimeStamp date={opportunity.publishedAt} className="text-gray-600" />
                {opportunity.status !== OpportunityStatus.CLOSED && (
                  <div
                    className="rounded-lg bg-gray-100 border border-gray-200 px-2 py-1.5 hover:bg-gray-200 transition-colors"
                    onClick={e => e.stopPropagation()}>
                    <Dropdown
                      trigger={<img src={Icons.THREE_DOT} alt="More options" className="cursor-pointer w-5 h-5 text-gray-600" />}
                      items={dropdownItems}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <Stack spacing={StackSpacing.SM}>
            <Heading
              level={HeadingLevel.H4}
              variant={HeadingVariant.SUBTITLE}
              weight={HeadingWeight.SEMIBOLD}
              color={HeadingColor.PRIMARY}
              className="text-sm">
              Job Description
            </Heading>
            <ExpandableText
              maxLength={100}
              className="text-gray-600 text-sm leading-relaxed"
              seeMoreText="Read more..."
              seeLessText="Read less"
              preserveWhitespace={true}>
              {opportunity.jobDescription}
            </ExpandableText>
          </Stack>

          <div className="flex flex-wrap gap-3">
            <Tag>{opportunity.formattedEmploymentType}</Tag>
            <Tag>{opportunity.formattedExperienceLevel}</Tag>
            <Tag>{opportunity.location || 'Remote'}</Tag>
          </div>

          {isDraft ? (
            <div className="flex justify-end">
              <Button variant={ButtonVariant.Primary} size={ButtonSize.SM} onClick={handlePreviewClick}>
                Publish
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <Link
                to={`/opportunities/${opportunity.id}?tab=${ApplicantTab.APPLIED}`}
                onMouseDownCapture={e => e.stopPropagation()}
                onClickCapture={e => e.stopPropagation()}
                aria-label="View applied applicants"
                className="text-left cursor-pointer block">
                <StatCard value={opportunity.applicationStats.applied} label="Applied Candidate" variant={StatCardVariant.Neutral} />
              </Link>
              <Link
                to={`/opportunities/${opportunity.id}?tab=${ApplicantTab.SHORTLISTED}`}
                onMouseDownCapture={e => e.stopPropagation()}
                onClickCapture={e => e.stopPropagation()}
                aria-label="View shortlisted applicants"
                className="text-left cursor-pointer block">
                <StatCard value={opportunity.applicationStats.shortlisted} label="Shortlisted" variant={StatCardVariant.Positive} />
              </Link>
              <Link
                to={`/opportunities/${opportunity.id}?tab=${ApplicantTab.REJECTED}`}
                onMouseDownCapture={e => e.stopPropagation()}
                onClickCapture={e => e.stopPropagation()}
                aria-label="View rejected applicants"
                className="text-left cursor-pointer block">
                <StatCard value={opportunity.applicationStats.rejected} label="Rejected" variant={StatCardVariant.Negative} />
              </Link>
            </div>
          )}
        </Stack>
      </Card>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Close Opportunity"
        message={`Are you sure you want to close "${opportunity.jobTitle}"? This action cannot be undone.`}
        confirmText="Close"
        cancelText="Cancel"
        isLoading={isDeleting}
        variant={ConfirmationVariant.DANGER}
      />
    </>
  );
};

export const OpportunityCard = React.memo(OpportunityCardComponent);
